/* eslint-disable unicorn/prevent-abbreviations, unicorn/no-null */

import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ActionIcon, Box, Group, Text, Tooltip } from '@mantine/core';
import { X } from 'lucide-react';
import SandboxChat from '@/modules/Sandbox';
import useSandboxStore from '@/modules/Sandbox/Sandbox.store.ts';
import type { TSandboxSize } from '@/modules/Sandbox/Sandbox.store.ts';
import {
    SANDBOX_DEFAULT_Y,
    SANDBOX_VIEWPORT_PADDING
} from '@/pages/Project/components/Sandbox/Sandbox.constants';
import { SandboxDragKind } from '@/pages/Project/components/Sandbox/Sandbox.enums';
import styles from '@/pages/Project/components/Sandbox/Sandbox.module.css';
import {
    clampSandboxPosition,
    clampSandboxSize
} from '@/pages/Project/components/Sandbox/Sandbox.utils';

interface DragStart {
    kind: SandboxDragKind;
    pointerX: number;
    pointerY: number;
    posX: number;
    posY: number;
    width: number;
    height: number;
}

const Sandbox: FC = () => {
    const isChatOpened = useSandboxStore((s) => s.isChatOpened);
    const toggleChat = useSandboxStore((s) => s.toggleChat);
    const position = useSandboxStore((s) => s.position);
    const setPosition = useSandboxStore((s) => s.setPosition);
    const size = useSandboxStore((s) => s.size);
    const setSize = useSandboxStore((s) => s.setSize);

    useEffect(() => {
        if (isChatOpened) {
            document.documentElement.classList.add('newo-chat-opened');
        } else {
            document.documentElement.classList.remove('newo-chat-opened');
        }
        return () => {
            document.documentElement.classList.remove('newo-chat-opened');
        };
    }, [isChatOpened]);

    useEffect(() => {
        if (!isChatOpened) {
            return;
        }
        if (position === null) {
            setPosition({
                x: Math.max(
                    SANDBOX_VIEWPORT_PADDING,
                    window.innerWidth - size.width - SANDBOX_VIEWPORT_PADDING
                ),
                y: SANDBOX_DEFAULT_Y
            });
            return;
        }
        const clamped = clampSandboxPosition(position, size);
        if (clamped.x !== position.x || clamped.y !== position.y) {
            setPosition(clamped);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChatOpened]);

    const [isInteracting, setIsInteracting] = useState(false);
    const dragStartRef = useRef<DragStart | null>(null);

    const beginGesture = useCallback(
        (kind: SandboxDragKind) => (e: React.MouseEvent<HTMLDivElement>) => {
            if (
                e.target instanceof HTMLElement &&
                e.target.closest('[data-sandbox-no-drag]')
            ) {
                return;
            }
            e.preventDefault();

            if (position === null) {
                return;
            }
            dragStartRef.current = {
                kind,
                pointerX: e.clientX,
                pointerY: e.clientY,
                posX: position.x,
                posY: position.y,
                width: size.width,
                height: size.height
            };
            setIsInteracting(true);

            const onMove = (event_: MouseEvent) => {
                const start = dragStartRef.current;
                if (!start) {
                    return;
                }
                const dx = event_.clientX - start.pointerX;
                const dy = event_.clientY - start.pointerY;

                if (start.kind === SandboxDragKind.Move) {
                    setPosition(
                        clampSandboxPosition(
                            { x: start.posX + dx, y: start.posY + dy },
                            { width: start.width, height: start.height }
                        )
                    );
                    return;
                }

                const nextSize: TSandboxSize = {
                    width:
                        start.kind === SandboxDragKind.ResizeBottom
                            ? start.width
                            : start.width + dx,
                    height:
                        start.kind === SandboxDragKind.ResizeRight
                            ? start.height
                            : start.height + dy
                };
                setSize(clampSandboxSize(nextSize));
            };
            const onUp = () => {
                dragStartRef.current = null;
                setIsInteracting(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [
            position?.x,
            position?.y,
            size.height,
            size.width,
            setPosition,
            setSize
        ]
    );

    if (!isChatOpened || position === null) {
        return null;
    }

    const renderedSize = clampSandboxSize(size);

    return (
        <Box
            aria-label="Sandbox window"
            className={clsx(
                styles.window,
                isInteracting && styles.isInteracting
            )}
            role="dialog"
            style={{
                left: position.x,
                top: position.y,
                width: renderedSize.width,
                height: renderedSize.height
            }}
        >
            <Box
                className={styles.titleBar}
                onMouseDown={beginGesture(SandboxDragKind.Move)}
            >
                <Text fw={600} size="xs">
                    Sandbox
                </Text>
                <Group gap={4} wrap="nowrap">
                    <Tooltip withinPortal label="Close" position="bottom">
                        <ActionIcon
                            data-sandbox-no-drag
                            aria-label="Close sandbox"
                            size="sm"
                            variant="subtle"
                            onClick={toggleChat}
                            onMouseDown={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <X size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            </Box>

            <Box className={styles.content}>
                <SandboxChat className={styles.chat} />
            </Box>

            <Box
                aria-label="Resize sandbox (right edge)"
                className={clsx(styles.resizeHandle, styles.resizeRight)}
                role="separator"
                onMouseDown={beginGesture(SandboxDragKind.ResizeRight)}
            />
            <Box
                aria-label="Resize sandbox (bottom edge)"
                className={clsx(styles.resizeHandle, styles.resizeBottom)}
                role="separator"
                onMouseDown={beginGesture(SandboxDragKind.ResizeBottom)}
            />
            <Box
                aria-label="Resize sandbox (bottom-right corner)"
                className={clsx(styles.resizeHandle, styles.resizeCorner)}
                role="separator"
                onMouseDown={beginGesture(SandboxDragKind.ResizeCorner)}
            />
        </Box>
    );
};

export default Sandbox;
